import React, { useState, useEffect } from 'react';
import axios from 'axios';
import UserLayout from '../../components/user/UserLayout';
import {
  TrendingUp, TrendingDown, Calendar, AlertCircle,
  CheckCircle, Clock, BarChart2
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

export default function ProfitHistoryPage() {
  const [data, setData] = useState({ profits: [], total: 0, stats: {} });
  const [todayData, setTodayData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchData = () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 20 });
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    axios.get(`/api/profit/my-history?${params}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    axios.get('/api/profit/today').then(r => setTodayData(r.data));
  }, []);

  useEffect(() => { fetchData(); }, [page, startDate, endDate]);

  const chartData = [...(data.profits || [])].reverse().map(p => ({
    date: new Date(p.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
    'Gross Profit': parseFloat(p.grossProfit?.toFixed(2) || 0),
    'Deduction': parseFloat(p.deductionAmount?.toFixed(2) || 0),
    'Net Profit': parseFloat(p.netProfit?.toFixed(2) || 0),
  }));

  const statusBadge = {
    processed: { text: 'Processed', class: 'bg-primary-500/20 text-primary-300', icon: <CheckCircle className="w-3 h-3" /> },
    insufficient_wallet: { text: 'Low Wallet', class: 'bg-red-500/20 text-red-300', icon: <AlertCircle className="w-3 h-3" /> },
    no_profit: { text: 'No Profit', class: 'bg-gray-500/20 text-gray-400', icon: <span>—</span> }
  };

  

  return (
    <UserLayout>
      <div className="animate-fade-in">
        <h1 className="text-2xl font-bold text-white mb-2">Daily Profit History</h1>
        <p className="text-gray-400 mb-6">Automatically fetched from MT5 — 40% is automatically deducted from the main wallet.</p>

        {/* Today's Card */}
        {todayData && (
          <div className={`rounded-2xl p-5 mb-6 border ${
            todayData.todayProfit
              ? todayData.todayProfit.grossProfit > 0
                ? 'bg-primary-500/10 border-primary-500/30'
                : 'bg-dark-700 border-white/5'
              : 'bg-amber-500/10 border-amber-500/30'
          }`}>
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-amber-400" />
              <h3 className="text-white font-semibold">Today's Status</h3>
            </div>
            {todayData.todayProfit ? (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-gray-400 text-xs">Gross Profit</p>
                  <p className="text-primary-400 font-bold text-lg">
                    ${todayData.todayProfit.grossProfit?.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs">40% Deducted</p>
                  <p className="text-red-400 font-bold text-lg">
                    -${todayData.todayProfit.deductionAmount?.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs">Net Profit</p>
                  <p className="text-white font-bold text-lg">
                    ${todayData.todayProfit.netProfit?.toFixed(2)}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-amber-300 text-sm">
                Today's profit has not been processed yet. It will happen automatically at 1:00 AM.
              </p>
            )}

            {/* Latest Equity */}
            {todayData.latestSnapshot && (
              <div className="mt-3 pt-3 border-t border-white/10 grid grid-cols-2 gap-3">
                <div>
                  <p className="text-gray-500 text-xs">Current Equity</p>
                  <p className="text-white font-semibold">
                    ${todayData.latestSnapshot.equity?.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Current Balance</p>
                  <p className="text-white font-semibold">
                    ${todayData.latestSnapshot.balance?.toFixed(2)}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Gross Profit', value: `$${(data.stats?.totalGross || 0).toFixed(2)}`, icon: TrendingUp, color: 'text-primary-400', bg: 'bg-primary-500/20' },
            { label: 'Total Deducted', value: `$${(data.stats?.totalDeducted || 0).toFixed(2)}`, icon: TrendingDown, color: 'text-red-400', bg: 'bg-red-500/20' },
            { label: 'Net Profit', value: `$${(data.stats?.totalNet || 0).toFixed(2)}`, icon: BarChart2, color: 'text-blue-400', bg: 'bg-blue-500/20' },
            { label: 'Profit Days', value: `${data.stats?.profitDays || 0} / ${data.stats?.totalDays || 0}`, icon: Calendar, color: 'text-amber-400', bg: 'bg-amber-500/20' },
          ].map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} className="bg-dark-700 border border-white/5 rounded-2xl p-4">
              <div className={`w-8 h-8 ${bg} rounded-xl flex items-center justify-center mb-2`}>
                <Icon className={`w-4 h-4 ${color}`} />
              </div>
              <p className="text-white font-bold">{value}</p>
              <p className="text-gray-500 text-xs mt-0.5">{label}</p>
            </div>
          ))}
        </div>

        {/* Chart */}
        {chartData.length > 1 && (
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-5 mb-6">
            <h3 className="text-white font-semibold mb-4">Profit Chart</h3>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="gp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="dd" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                <XAxis dataKey="date" stroke="#6b7280" tick={{ fontSize: 11 }} />
                <YAxis stroke="#6b7280" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#1a2540', border: '1px solid #ffffff20', borderRadius: '8px', color: '#fff' }} />
                <Legend />
                <Area type="monotone" dataKey="Gross Profit" stroke="#22c55e" fill="url(#gp)" strokeWidth={2} />
                <Area type="monotone" dataKey="Deduction" stroke="#ef4444" fill="url(#dd)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Date Filter */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl p-4 mb-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">From Date</label>
              <input type="date" value={startDate}
                onChange={e => { setStartDate(e.target.value); setPage(1); }}
                className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">To Date</label>
              <input type="date" value={endDate}
                onChange={e => { setEndDate(e.target.value); setPage(1); }}
                className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary-500" />
            </div>
            <button onClick={() => { setStartDate(''); setEndDate(''); setPage(1); }}
              className="px-3 py-2 bg-dark-600 border border-white/10 text-gray-400 hover:text-white rounded-xl text-sm">
              Clear
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-white/5">
            <h3 className="text-white font-semibold">Profit Records ({data.total})</h3>
          </div>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : data.profits?.length === 0 ? (
            <div className="py-12 text-center">
              <TrendingUp className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500">No profit records found</p>
              <p className="text-gray-600 text-sm mt-1">MT5 connection submitted — data will arrive automatically.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="text-left px-4 py-3 text-gray-400 font-medium">Date</th>
                      <th className="text-right px-4 py-3 text-gray-400 font-medium">Opening Equity</th>
                      <th className="text-right px-4 py-3 text-gray-400 font-medium">Closing Equity</th>
                      <th className="text-right px-4 py-3 text-gray-400 font-medium">Gross Profit</th>
                      <th className="text-right px-4 py-3 text-gray-400 font-medium">40% Deducted</th>
                      <th className="text-right px-4 py-3 text-gray-400 font-medium">Wallet After</th>
                      <th className="text-center px-4 py-3 text-gray-400 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.profits.map(p => {
                      const badge = statusBadge[p.status] || statusBadge.processed;
                      return (
                        <tr key={p._id} className="border-b border-white/5 hover:bg-white/2">
                          <td className="px-4 py-3 text-gray-300 text-sm">
                            {new Date(p.date).toLocaleDateString('en-IN', {
                              day: '2-digit', month: 'short', year: 'numeric'
                            })}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-400">
                            ${(p.openingEquity || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-400">
                            ${(p.closingEquity || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-right text-primary-400 font-semibold">
                            {p.grossProfit > 0 ? `+$${p.grossProfit.toFixed(2)}` : '$0'}
                          </td>
                          <td className="px-4 py-3 text-right text-red-400 font-semibold">
                            {p.deductionAmount > 0 ? `-$${p.deductionAmount.toFixed(2)}` : '—'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className={`font-semibold ${(p.walletAfter || 0) >= 0 ? 'text-white' : 'text-red-400'}`}>
                              ${(p.walletAfter || 0).toFixed(2)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.class}`}>
                              {badge.icon}{badge.text}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {data.total > 20 && (
                <div className="p-4 border-t border-white/5 flex justify-between items-center">
                  <p className="text-gray-400 text-sm">Page {page} of {data.pages}</p>
                  <div className="flex gap-2">
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                      className="px-3 py-1.5 bg-dark-600 text-white rounded-lg text-sm disabled:opacity-50">Prev</button>
                    <button onClick={() => setPage(p => p + 1)} disabled={page >= data.pages}
                      className="px-3 py-1.5 bg-dark-600 text-white rounded-lg text-sm disabled:opacity-50">Next</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </UserLayout>
  );
}
